import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Settings, Save, Monitor, MapPin, Phone, FileText, Printer, Percent, Warehouse } from 'lucide-react';

export default function POSSettings() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    pos_enabled: false,
    default_warehouse_id: '',
    tax_percentage: 0,
    store_name: '',
    store_address: '',
    store_phone: '',
    receipt_footer: 'Terima kasih atas kunjungan Anda!',
    auto_print_receipt: true,
    receipt_paper_width: '80mm',
    receipt_show_logo: false,
    receipt_logo_url: '',
  });
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [settingsRes, whRes] = await Promise.all([
        api.get('/tenant/pos/settings'),
        api.get('/tenant/inventory/warehouses').catch(() => ({ data: { warehouses: [] } })),
      ]);
      const s = settingsRes.data;
      setForm({
        pos_enabled: s.pos_enabled || false,
        default_warehouse_id: s.default_warehouse_id || '',
        tax_percentage: s.tax_percentage || 0,
        store_name: s.store_name || '',
        store_address: s.store_address || '',
        store_phone: s.store_phone || '',
        receipt_footer: s.receipt_footer || 'Terima kasih atas kunjungan Anda!',
        auto_print_receipt: s.auto_print_receipt !== false,
        receipt_paper_width: s.receipt_paper_width === '58mm' ? '58mm' : '80mm',
        receipt_show_logo: !!s.receipt_show_logo,
        receipt_logo_url: s.receipt_logo_url || '',
      });
      setWarehouses(whRes.data.warehouses || []);
    } catch { /* interceptor handles */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/pos/settings', {
        ...form,
        tax_percentage: parseFloat(form.tax_percentage || 0),
        default_warehouse_id: form.default_warehouse_id || null,
      });
      toast({ title: 'Tersimpan', description: 'Pengaturan POS berhasil diperbarui' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Gagal menyimpan', variant: 'destructive' });
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div className="space-y-6" data-testid="pos-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Settings size={24} /> Pengaturan POS</h1>
          <p className="text-muted-foreground mt-1">Konfigurasi sistem kasir untuk toko offline</p>
        </div>
        <Button className="bg-[#2D6CDF] hover:bg-blue-700" onClick={save} disabled={saving}>
          <Save size={16} className="mr-2" /> {saving ? 'Menyimpan...' : 'Simpan'}
        </Button>
      </div>

      {/* Enable POS */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Monitor size={20} className="text-[#2D6CDF]" />
              </div>
              <div>
                <p className="font-semibold">Aktifkan POS</p>
                <p className="text-sm text-slate-500">Aktifkan sistem kasir untuk transaksi di toko fisik</p>
              </div>
            </div>
            <Switch checked={form.pos_enabled} onCheckedChange={v => setForm(f => ({ ...f, pos_enabled: v }))} />
          </div>
        </CardContent>
      </Card>

      {/* Store Info */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} /> Informasi Toko (Struk)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nama Toko</Label>
              <Input value={form.store_name} onChange={e => setForm(f => ({ ...f, store_name: e.target.value }))} placeholder="Nama toko di struk" />
            </div>
            <div>
              <Label>No. Telepon</Label>
              <Input value={form.store_phone} onChange={e => setForm(f => ({ ...f, store_phone: e.target.value }))} placeholder="08xx" />
            </div>
          </div>
          <div>
            <Label>Alamat Toko</Label>
            <Input value={form.store_address} onChange={e => setForm(f => ({ ...f, store_address: e.target.value }))} placeholder="Alamat lengkap toko" />
          </div>
          <div>
            <Label>Footer Struk</Label>
            <Input value={form.receipt_footer} onChange={e => setForm(f => ({ ...f, receipt_footer: e.target.value }))} placeholder="Pesan di bawah struk" />
          </div>
        </CardContent>
      </Card>

      {/* Warehouse & Tax */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Warehouse size={16} /> Gudang & Pajak</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Gudang Default POS</Label>
              <Select value={form.default_warehouse_id || 'auto'} onValueChange={v => setForm(f => ({ ...f, default_warehouse_id: v === 'auto' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Otomatis (Gudang Default)</SelectItem>
                  {warehouses.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.name} {w.is_default ? '(Default)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Stok akan dikurangi dari gudang ini saat transaksi POS</p>
            </div>
            <div>
              <Label>Pajak (%)</Label>
              <Input type="number" value={form.tax_percentage} onChange={e => setForm(f => ({ ...f, tax_percentage: e.target.value }))} placeholder="0" />
              <p className="text-xs text-slate-400 mt-1">Pajak sudah termasuk dalam harga (tax inclusive)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receipt */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Printer size={16} /> Struk / Receipt</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Auto Print Struk</p>
              <p className="text-sm text-slate-500">Otomatis cetak struk setelah transaksi berhasil</p>
            </div>
            <Switch checked={form.auto_print_receipt} onCheckedChange={v => setForm(f => ({ ...f, auto_print_receipt: v }))} />
          </div>

          <div className="border-t pt-4">
            <Label>Lebar Kertas Thermal</Label>
            <Select
              value={form.receipt_paper_width}
              onValueChange={v => setForm(f => ({ ...f, receipt_paper_width: v }))}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="58mm">58mm (printer mini / mobile)</SelectItem>
                <SelectItem value="80mm">80mm (printer kasir umum)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1">Sesuaikan dengan ukuran kertas thermal printer Anda. Setelah ubah, layout struk akan menyesuaikan otomatis.</p>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-medium">Tampilkan Logo di Struk</p>
                <p className="text-sm text-slate-500">Logo akan dicetak hitam-putih di atas struk</p>
              </div>
              <Switch checked={form.receipt_show_logo} onCheckedChange={v => setForm(f => ({ ...f, receipt_show_logo: v }))} />
            </div>
            {form.receipt_show_logo && (
              <div>
                <Label>URL Logo</Label>
                <Input
                  value={form.receipt_logo_url}
                  onChange={e => setForm(f => ({ ...f, receipt_logo_url: e.target.value }))}
                  placeholder="https://... (PNG/JPG, max 40mm × 15mm)"
                  className="mt-1 font-mono text-xs"
                />
                <p className="text-xs text-slate-400 mt-1">Upload dulu di Media/Storage, lalu paste URL-nya di sini.</p>
              </div>
            )}
          </div>

          <div className="border-t pt-4 bg-amber-50 -m-6 mt-4 p-4 rounded-b-lg">
            <p className="text-xs font-semibold text-amber-900 mb-1">💡 Tips cetak ke printer thermal:</p>
            <ul className="text-xs text-amber-900 list-disc pl-4 space-y-0.5">
              <li>Pastikan driver printer thermal sudah terinstall di komputer/laptop kasir</li>
              <li>Saat dialog Print muncul, klik "More settings" → uncheck "Headers and footers", set Margins: None</li>
              <li>Untuk hasil terbaik, pilih printer thermal di dropdown Destination (bukan "Save as PDF")</li>
              <li>Printer USB direct (no driver) belum didukung — coming soon di update berikutnya</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
